/**
 * Shared offer-term calculations used by UnderwriterForm (Offer Terms) and
 * OfferCalculator (AI Analysis top row). Ensures 3-way linking (added leverage,
 * funding amount, merchant installment) and frequency-aware math stay in sync.
 */

export const DEFAULT_BUSINESS_DAYS_PER_MONTH = 20;

/** Offer factor is stored with at most four decimal places, so 1.4999 is valid. */
export const OFFER_FACTOR_STEP = "0.0001";

export function roundOfferFactor(value: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return Number(n.toFixed(4));
}

/** Up to four decimal places, without trailing zeros (1.4999, 1.49, 1.5). */
export function formatOfferFactor(value: number): string {
  const n = roundOfferFactor(Number(value));
  if (!n || Number.isNaN(n)) return "";
  return String(n);
}

export function resolveBusinessDaysPerMonth(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_BUSINESS_DAYS_PER_MONTH;
}

/** Term is business days: 5/week, businessDaysPerMonth/month. */
export function frequencyToMonthlyMultiplier(
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): number {
  if (frequency === "daily") return resolveBusinessDaysPerMonth(businessDaysPerMonth);
  if (frequency === "weekly") return 4;
  if (frequency === "monthly") return 1;
  return 1;
}

export function positionMonthlyObligation(
  payment: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): number {
  return payment * frequencyToMonthlyMultiplier(frequency, businessDaysPerMonth);
}

export function getPaymentPeriods(
  term: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): number {
  const days = resolveBusinessDaysPerMonth(businessDaysPerMonth);
  if (frequency === "daily") return term;
  if (frequency === "weekly") return Math.ceil(term / 5);
  if (frequency === "monthly") return Math.ceil(term / days);
  return term;
}

/** Days per displayed term unit when Term is frequency-aware (not always-days). */
export function termDisplayDivisor(
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): number {
  if (frequency === "weekly") return 5;
  if (frequency === "monthly") return resolveBusinessDaysPerMonth(businessDaysPerMonth);
  return 1;
}

export function termUnitLabel(frequency: string): "Days" | "Weeks" | "Months" {
  if (frequency === "weekly") return "Weeks";
  if (frequency === "monthly") return "Months";
  return "Days";
}

/** Internal business days → value shown in the Term input. */
export function daysToTermDisplay(
  termDays: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH,
  alwaysDays = true
): number {
  const days = Math.max(0, Math.floor(Number(termDays)) || 0);
  if (alwaysDays || !(days > 0)) return days;
  const divisor = termDisplayDivisor(frequency, businessDaysPerMonth);
  return Math.max(1, Math.round(days / divisor));
}

/** Term input value → internal business days. */
export function termDisplayToDays(
  display: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH,
  alwaysDays = true
): number {
  const n = Math.max(0, Math.floor(Number(display)) || 0);
  if (!(n > 0)) return 0;
  if (alwaysDays) return n;
  return n * termDisplayDivisor(frequency, businessDaysPerMonth);
}

export function calculateFromAddedLeverage(
  addedLeverageInput: number,
  avgMonthlyRevenue: number,
  factor: number,
  term: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): { fundingAmount: number; merchantInstallment: number } {
  if (avgMonthlyRevenue <= 0) return { fundingAmount: 0, merchantInstallment: 0 };

  const days = resolveBusinessDaysPerMonth(businessDaysPerMonth);
  const monthlyPayment = (addedLeverageInput / 100) * avgMonthlyRevenue;
  const dailyPayment = monthlyPayment / days;
  const totalPayback = dailyPayment * term;
  const fundingAmount = totalPayback / factor;

  const paymentPeriods = getPaymentPeriods(term, frequency, days);
  const merchantInstallment = Math.ceil(totalPayback / paymentPeriods);

  return {
    fundingAmount: Math.round(fundingAmount),
    merchantInstallment,
  };
}

/** The payment that pays `fundingAmount` back at `factor` over the term's payments. */
export function merchantInstallmentFor(
  fundingAmount: number,
  factor: number,
  term: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): number {
  const days = resolveBusinessDaysPerMonth(businessDaysPerMonth);
  return Math.ceil((fundingAmount * factor) / getPaymentPeriods(term, frequency, days));
}

export function calculateFromFundingAmount(
  fundingAmount: number,
  avgMonthlyRevenue: number,
  factor: number,
  term: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): { addedLeverageInput: number; merchantInstallment: number } {
  if (avgMonthlyRevenue <= 0) return { addedLeverageInput: 0, merchantInstallment: 0 };

  const days = resolveBusinessDaysPerMonth(businessDaysPerMonth);
  const totalPayback = fundingAmount * factor;
  const dailyPayment = totalPayback / term;
  const monthlyPayment = dailyPayment * days;
  const addedLeverageInput = (monthlyPayment / avgMonthlyRevenue) * 100;

  return {
    addedLeverageInput: Math.round(addedLeverageInput),
    merchantInstallment: merchantInstallmentFor(fundingAmount, factor, term, frequency, days),
  };
}

/** The funding that `merchantInstallment` over the term's payments pays back at `factor`. */
export function fundingAmountFor(
  merchantInstallment: number,
  factor: number,
  term: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): number {
  const days = resolveBusinessDaysPerMonth(businessDaysPerMonth);
  return Math.round((merchantInstallment * getPaymentPeriods(term, frequency, days)) / factor);
}

export function calculateFromMerchantInstallment(
  merchantInstallment: number,
  avgMonthlyRevenue: number,
  factor: number,
  term: number,
  frequency: string,
  businessDaysPerMonth: number = DEFAULT_BUSINESS_DAYS_PER_MONTH
): { addedLeverageInput: number; fundingAmount: number } {
  if (avgMonthlyRevenue <= 0) return { addedLeverageInput: 0, fundingAmount: 0 };

  const days = resolveBusinessDaysPerMonth(businessDaysPerMonth);
  const paymentPeriods = getPaymentPeriods(term, frequency, days);
  const totalPayback = merchantInstallment * paymentPeriods;
  const dailyPayment = totalPayback / term;
  const monthlyPayment = dailyPayment * days;
  const addedLeverageInput = (monthlyPayment / avgMonthlyRevenue) * 100;

  return {
    addedLeverageInput: Math.round(addedLeverageInput),
    fundingAmount: fundingAmountFor(merchantInstallment, factor, term, frequency, days),
  };
}
